import React, { useState, useMemo } from 'react';
import TeachingTooltip from '@/components/TeachingTooltip';
import { useTheme } from '@/context/ThemeContext';

const DEG = Math.PI / 180;
const RAD = 180 / Math.PI;
const cadd    = (a, b) => ({ re: a.re + b.re, im: a.im + b.im });
const cmul    = (a, b) => ({ re: a.re*b.re - a.im*b.im, im: a.re*b.im + a.im*b.re });
const cscale  = (c, s) => ({ re: c.re*s, im: c.im*s });
const cmag    = (c)    => Math.sqrt(c.re*c.re + c.im*c.im);
const cang    = (c)    => Math.atan2(c.im, c.re) * RAD;
const fromPolar = (mag, angDeg) => ({ re: mag*Math.cos(angDeg*DEG), im: mag*Math.sin(angDeg*DEG) });
const A_OP  = fromPolar(1, 120);
const A2_OP = fromPolar(1, 240);

function decompose(Va, Vb, Vc) {
  const V0 = cscale(cadd(Va, cadd(Vb, Vc)), 1/3);
  const V1 = cscale(cadd(Va, cadd(cmul(A_OP, Vb), cmul(A2_OP, Vc))), 1/3);
  const V2 = cscale(cadd(Va, cadd(cmul(A2_OP, Vb), cmul(A_OP, Vc))), 1/3);
  return { V0, V1, V2 };
}

// Combined six-phasor SVG
const LivePhasorSVG = ({ phaseVecs, seqVecs, dark }) => {
  const CX = 155, CY = 155, RMAX = 115;
  const bg = dark ? '#0f172a' : '#ffffff';
  const ax = dark ? '#1e293b' : '#e2e8f0';
  const lc = dark ? '#475569' : '#94a3b8';

  // Find max magnitude to auto-scale
  const allMags = [...phaseVecs, ...seqVecs].map(({ vec }) => cmag(vec));
  const maxM = Math.max(...allMags, 0.1);
  const scale = RMAX / (maxM * 1.15);

  const drawArrow = (vec, color, lbl, dashed = false) => {
    if (cmag(vec) < 0.015) return null;
    const rad  = Math.atan2(vec.im, vec.re);
    const r    = cmag(vec) * scale;
    const tx   = CX + r*Math.cos(rad);
    const ty   = CY - r*Math.sin(rad);
    const bx   = CX + (r-11)*Math.cos(rad);
    const by   = CY - (r-11)*Math.sin(rad);
    const hw   = 5;
    const pts  = `${tx},${ty} ${bx+hw*Math.sin(rad)},${by+hw*Math.cos(rad)} ${bx-hw*Math.sin(rad)},${by-hw*Math.cos(rad)}`;
    return (
      <g key={lbl}>
        <line x1={CX} y1={CY} x2={bx} y2={by} stroke={color} strokeWidth={dashed ? '1.8' : '2.5'}
          strokeLinecap="round" strokeDasharray={dashed ? '6 3' : undefined} />
        <polygon points={pts} fill={color} />
        <text x={tx + 15*Math.cos(rad)} y={ty - 15*Math.sin(rad) + 4}
          fontSize="10.5" fontWeight="700" fontFamily="IBM Plex Mono" fill={color} textAnchor="middle">
          {lbl}
        </text>
      </g>
    );
  };

  return (
    <svg viewBox="0 0 310 310" className="w-full max-w-[280px] mx-auto block">
      <rect width="310" height="310" fill={bg} rx="12" />
      {[0.5, 1.0].map(f => (
        <circle key={f} cx={CX} cy={CY} r={RMAX*f/1.15} fill="none" stroke={ax} strokeWidth="0.8" strokeDasharray="3 4" />
      ))}
      <line x1={CX-RMAX-8} y1={CY} x2={CX+RMAX+8} y2={CY} stroke={ax} strokeWidth="0.8" />
      <line x1={CX} y1={CY-RMAX-8} x2={CX} y2={CY+RMAX+8} stroke={ax} strokeWidth="0.8" />
      <text x={CX+RMAX+5} y={CY-3} fontSize="8" fill={lc} fontFamily="IBM Plex Mono">+Re</text>
      <text x={CX+4} y={CY-RMAX-3} fontSize="8" fill={lc} fontFamily="IBM Plex Mono">+Im</text>
      {phaseVecs.map(({ vec, color, label }) => drawArrow(vec, color, label, false))}
      {seqVecs.map(({ vec, color, label }) => drawArrow(vec, color, label, true))}
      <circle cx={CX} cy={CY} r="3.5" fill={dark ? '#475569' : '#94a3b8'} />
    </svg>
  );
};

const SequencePhasorViz = () => {
  const { dark } = useTheme();

  const [Va_mag, setVaMag] = useState(1.0);
  const [Va_ang, setVaAng] = useState(0);
  const [Vb_mag, setVbMag] = useState(1.0);
  const [Vb_ang, setVbAng] = useState(-120);
  const [Vc_mag, setVcMag] = useState(1.0);
  const [Vc_ang, setVcAng] = useState(120);

  const { Va, Vb, Vc, V0, V1, V2 } = useMemo(() => {
    const Va = fromPolar(Va_mag, Va_ang);
    const Vb = fromPolar(Vb_mag, Vb_ang);
    const Vc = fromPolar(Vc_mag, Vc_ang);
    const { V0, V1, V2 } = decompose(Va, Vb, Vc);
    return { Va, Vb, Vc, V0, V1, V2 };
  }, [Va_mag, Va_ang, Vb_mag, Vb_ang, Vc_mag, Vc_ang]);

  const phaseVecs = [
    { vec: Va, color: '#64748b', label: 'Va' },
    { vec: Vb, color: '#6366f1', label: 'Vb' },
    { vec: Vc, color: '#f59e0b', label: 'Vc' },
  ];
  const seqVecs = [
    { vec: V1, color: '#0d9488', label: 'V₁' },
    { vec: V2, color: '#ea580c', label: 'V₂' },
    { vec: V0, color: '#7c3aed', label: 'V₀' },
  ];

  const sliderCls = 'flex-1 accent-teal-600';
  const numCls = 'w-16 text-xs border border-slate-300 dark:border-slate-600 rounded px-1.5 py-1 bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-right focus:ring-1 focus:ring-teal-500 outline-none font-mono';

  const controls = [
    { label: 'Va', mag: Va_mag, setMag: setVaMag, ang: Va_ang, setAng: setVaAng, color: '#64748b',
      tt_mag_what: 'Magnitude of Va in per-unit. 1.0 pu = rated voltage.',
      tt_mag_try: 'Set Va_mag = 0 to simulate a phase-A-to-ground fault.',
      tt_ang_what: 'Phase angle of Va (reference phasor). Typically 0° by convention.',
      tt_ang_try: 'Rotate Va to 45° — watch how the sequence components rotate too.' },
    { label: 'Vb', mag: Vb_mag, setMag: setVbMag, ang: Vb_ang, setAng: setVbAng, color: '#6366f1',
      tt_mag_what: 'Magnitude of Vb in per-unit. Balanced: same as Va.',
      tt_mag_try: 'Change Vb_mag to 0.5 pu — creates strong V₂ (negative-sequence) component.',
      tt_ang_what: 'Phase angle of Vb. Balanced: -120° (lags Va by 120°).',
      tt_ang_try: 'Try Vb_ang = -100° to introduce small unbalance and watch V₂ grow.' },
    { label: 'Vc', mag: Vc_mag, setMag: setVcMag, ang: Vc_ang, setAng: setVcAng, color: '#f59e0b',
      tt_mag_what: 'Magnitude of Vc in per-unit. Balanced: same as Va and Vb.',
      tt_mag_try: 'Set Vc_mag = 1.3 pu — simulates overvoltage on one phase.',
      tt_ang_what: 'Phase angle of Vc. Balanced: +120° (leads Va by 120°).',
      tt_ang_try: 'Try all three phasors at different magnitudes (1.0, 0.7, 1.3) to see maximum unbalance.' },
  ];

  return (
    <div data-testid="sequence-phasor-viz" className="space-y-5">
      {/* Sliders */}
      <div className="space-y-4">
        {controls.map(({ label, mag, setMag, ang, setAng, color, tt_mag_what, tt_mag_try, tt_ang_what, tt_ang_try }) => (
          <div key={label} className="space-y-2">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-sm font-bold font-mono" style={{ color }}>{label}</span>
              <span className="text-xs text-slate-500 dark:text-slate-500">
                {mag.toFixed(3)} pu ∠{ang.toFixed(1)}°
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500 w-8 font-mono">|mag|</span>
                <input data-testid={`${label.toLowerCase()}-mag-slider`} type="range"
                  min="0" max="2" step="0.01" value={mag}
                  onChange={e => setMag(Number(e.target.value))} className={sliderCls} />
                <input type="number" value={mag} step="0.01" min="0" max="2"
                  onChange={e => setMag(Math.max(0, Math.min(2, Number(e.target.value))))} className={numCls} />
                <TeachingTooltip showIcon whatThis={tt_mag_what} tryThis={tt_mag_try}
                  whyMatters="Magnitude unbalance primarily creates negative-sequence component (V₂). Strong V₂ causes motor heating."
                  connectedTo="→ Updates V₁, V₂, V₀ outputs and the phasor diagram in real time." />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500 w-8 font-mono">ang°</span>
                <input data-testid={`${label.toLowerCase()}-ang-slider`} type="range"
                  min="-180" max="180" step="1" value={ang}
                  onChange={e => setAng(Number(e.target.value))} className={sliderCls} />
                <input type="number" value={ang} step="1" min="-180" max="180"
                  onChange={e => setAng(Math.max(-180, Math.min(180, Number(e.target.value))))} className={numCls} />
                <span className="text-xs text-slate-500 font-mono">°</span>
                <TeachingTooltip showIcon whatThis={tt_ang_what} tryThis={tt_ang_try}
                  whyMatters="Phase-angle unbalance creates both negative and zero-sequence components depending on the asymmetry type."
                  connectedTo="→ Updates all sequence component angles in the diagram and output table." />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Diagram + results side by side */}
      <div className="flex flex-col sm:flex-row gap-5 items-start">
        <div className="w-full sm:w-auto">
          <p className="text-xs text-slate-500 dark:text-slate-500 mb-1.5 text-center font-mono">
            Phase phasors (solid) + sequence (dashed)
          </p>
          <LivePhasorSVG phaseVecs={phaseVecs} seqVecs={seqVecs} dark={dark} />
        </div>

        <div className="flex-1 w-full space-y-2">
          <p className="text-xs font-semibold text-slate-500 dark:text-slate-500 uppercase tracking-widest">Sequence outputs</p>
          {[
            { label: 'V₁ Positive', vec: V1, color: '#0d9488', bg: 'bg-teal-50 dark:bg-teal-900/20 border-teal-200 dark:border-teal-800', tid: 'viz-v1-result' },
            { label: 'V₂ Negative', vec: V2, color: '#ea580c', bg: 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800', tid: 'viz-v2-result' },
            { label: 'V₀ Zero',     vec: V0, color: '#7c3aed', bg: 'bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800', tid: 'viz-v0-result' },
          ].map(({ label, vec, color, bg, tid }) => (
            <div key={tid} data-testid={tid} className={`rounded-md p-3 border ${bg}`}>
              <span className="text-xs font-bold font-mono" style={{ color }}>{label}  </span>
              <span className="text-xs font-mono" style={{ color }}>
                {cmag(vec).toFixed(4)} pu ∠{cang(vec).toFixed(2)}°
              </span>
            </div>
          ))}
          <div className="bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700 rounded-md p-3 mt-2">
            <p className="text-[10px] font-semibold text-slate-600 dark:text-slate-400 mb-1">Imbalance ratio</p>
            <p className="text-xs font-mono text-slate-700 dark:text-slate-300">
              V₂/V₁ = {cmag(V1) > 0.001 ? (cmag(V2)/cmag(V1)*100).toFixed(1) : '—'}%
              {' · '}V₀/V₁ = {cmag(V1) > 0.001 ? (cmag(V0)/cmag(V1)*100).toFixed(1) : '—'}%
            </p>
            <p className="text-[9px] text-slate-500 mt-1">Grid limit: V₂/V₁ &lt; 1–2% (NERC, IEC 61000-3-13)</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SequencePhasorViz;
